/**
 * PreliminaryCarePlanTab.jsx
 * --------------------------------------------------
 * DSHS / RN / AFH Preliminary Care Plan
 *
 * Ã¢Å“â€ Medical Summary (read-only from Profile)
 * Ã¢Å“â€ Base Care Plan (risk + mitigation)
 * Ã¢Å“â€ Transactional Care History (add/edit rows)
 * Ã¢Å“â€ Incident-report awareness (prompt only)
 * Ã¢Å“â€ Print-safe (no buttons / no entry forms)
 */

import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  Button,
  Stack,
  Divider,
  Paper,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Snackbar,
  Alert,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import { useReactToPrint } from "react-to-print";
import { useMutation, gql } from "@apollo/client";
import Tesseract from "tesseract.js";
import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const SAVE_CARE_PLAN = gql`
  mutation SaveCarePlan($data: ResidentCarePlanInput!) {
    saveCarePlan(data: $data) {
      id
      residentId
      primaryDiagnosis
      secondaryDiagnoses
      allergies
      specialDiet
      riskLevel
      riskFactors
      mitigationStrategy
      mobilityStatus
      cognitiveStatus
      codeStatus
      preliminaryServicePlan
    }
  }
`;

/* ================= CONSTANTS ================= */
const parseCommaSeparatedList = (value = "") =>
  String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const SKIN_BODY_ASSESSMENT_OPTIONS = [
  "Dry Skin",
  "Scaly Skin",
  "Oily Skin",
  "Fungus",
  "Yeast",
  "Reddened Area",
  "Pressure Ulcer",
  "Blister",
  "Bruise",
  "Scratch",
  "Skin Tear",
  "Burn",
  "Swelling",
  "Mass/Tumor",
  "Scar",
  "Mole",
  "Other",
];

const MEDICATION_SUPPORT_OPTIONS = [
  "Self",
  "Assist",
  "Administration/RND",
  "Use Psychotropic Medication(s)",
  "BSL Checks/Monitoring",
  "Non-Insulin Dep. Diabetes",
  "Insulin Dep. Diabetes",
  "Lock Box in Room",
];

const MOBILITY_FLAG_OPTIONS = [
  "Walks",
  "Independent",
  "Assistance",
  "Fall Risk",
  "Monitor Activity",
  "Supervision",
  "Call Bell",
  "Gait Belt",
  "Bed Alarm",
  "Motion Sensor",
  "Exit Alarm",
  "Floor Mat",
  "Cane",
  "FW Walker",
  "Walker with Seat",
  "Wheelchair",
  "Power Chair",
  "Chair Bound",
];

const TOILETING_FLAG_OPTIONS = [
  "Independent",
  "Assistance",
  "Continent Bowel",
  "Continent Bladder",
  "Incontinent Bowel",
  "Incontinent Bladder",
  "Day",
  "Night",
  "Uses Pads",
  "Uses Undergarment",
  "Peri Care",
  "Commode",
  "Toileting Schedule",
  "Catheter",
  "Ostomy",
];

const BEHAVIOR_FLAG_OPTIONS = [
  "Sleep Disturbance",
  "Short Term Memory Loss",
  "Long Term Memory Loss",
  "Decisions Impaired",
  "Disrobes",
  "Disruptive Behavior",
  "Disorientation",
  "Cueing/Reminders",
  "Resistive",
  "Yelling/Screaming",
  "Cussing/Swearing",
  "Throws Items",
  "Breaks Items",
  "Combative",
  "Spits",
  "Bites",
  "Scratches",
  "Pinches",
  "Hoarding",
  "Hiding Items",
  "No Boundaries",
  "Unaware of Personal Space",
  "Unaware of Consequences",
  "Exit Seeking",
  "Wanders",
  "Sexual Acts Out",
];

const MOOD_FLAG_OPTIONS = [
  "Anxious",
  "Agitated",
  "Calm",
  "Cheerful",
  "Depressed",
  "Irritable",
  "Lonely",
  "Negative",
  "Optimistic",
  "Positive",
  "Pessimistic",
  "Sad",
  "Mood Swings",
];

const TRANSPORTATION_FLAG_OPTIONS = [
  "AFH",
  "Family/POA/Guardian",
  "Para Transit",
  "Independent",
  "Needs Door to Door Service",
  "Needs Hand to Hand Service",
];

const EVACUATION_FLAG_OPTIONS = ["Independent", "Assistance"];
const TELEPHONE_FLAG_OPTIONS = [
  "Independent",
  "Assistance",
  "Total",
  "Take Messages/Make Appointments",
  "Own Phone",
];
const SKIN_PREFERENCE_OPTIONS = [
  "Routine",
  "Dry",
  "Bruises Easily",
  "Scratches",
  "Special",
  "Self",
  "Assist",
  "Total",
];
const COMMUNICATION_FLAG_OPTIONS = [
  "Speaks Well",
  "Understands Others",
  "Aphasia",
  "Delayed Speech",
  "Mumbles",
  "Speechless",
  "Hand Gestures/Sign Language",
  "Use Visual Aids",
  "Screams/Yells",
  "Uses Foul Language/Swears",
];
const HEARING_FLAG_OPTIONS = [
  "Hears Well",
  "Adequate",
  "Uses Amplifier",
  "Partially Deaf Left",
  "Partially Deaf Right",
  "Hearing Aid Left",
  "Hearing Aid Right",
  "Fully Deaf",
];
const VISION_FLAG_OPTIONS = [
  "Sees Well",
  "Adequate",
  "Reading Glasses",
  "Always Wears Glasses",
  "Needs Large Print",
  "Blind",
  "Cataracts Left",
  "Cataracts Right",
];
const HYGIENE_FLAG_OPTIONS = [
  "Comb/Brush Hair",
  "Wash Hands",
  "Wash Face",
  "Shave",
  "Fingernails",
  "Toenails",
  "Podiatrist",
  "Resistive",
  "Make-Up",
  "Cologne/Perfume",
  "Apply Deodorant",
  "Self",
  "Assist",
  "Total",
];
const MOUTH_FLAG_OPTIONS = [
  "Own Teeth",
  "No Teeth/Dentures",
  "Partial Upper",
  "Partial Lower",
  "Denture Upper",
  "Denture Lower",
  "Just Brush",
  "Soak",
  "Resistive",
  "Self",
  "Assist",
  "Total",
];
const MEDICAL_DEVICE_FLAG_OPTIONS = [
  "Hospital Bed",
  "Bed Rail Left",
  "Bed Rail Right",
  "Hoyer Lift",
  "Transfer Pole",
  "Trapeze",
  "Extra Pillows",
  "Extra Blankets",
];
const TRANSFER_FLAG_OPTIONS = [
  "Transfer Independent",
  "Transfer Stand-By Assist",
  "Transfer Needs Steadying",
  "Transfer Needs Assistance",
  "Positioning Independent",
  "Positioning Assistance",
  "Day",
  "Night",
];
const BATHING_FLAG_OPTIONS = [
  "Shower",
  "Bath Tub",
  "Bed Bath",
  "Bath Bench",
  "Bath Chair",
  "Resistive",
  "Self",
  "Assist",
  "Total",
];
const DRESSING_FLAG_OPTIONS = [
  "Upper",
  "Lower",
  "Compression Socks",
  "Brace",
  "Prosthesis",
  "Orthotics",
  "Self",
  "Assist",
  "Total",
];
const EATING_FLAG_OPTIONS = [
  "Feeds Self",
  "Cut Up Food",
  "Assist",
  "Total",
  "Regular Diet",
  "Advanced (Soft)",
  "Mechanical Altered (Moist)",
  "Puree Diet",
  "Diabetic",
  "Low Salt",
  "Shirt Protector",
];
const DRINKING_FLAG_OPTIONS = [
  "Normal Fluids (Thin)",
  "Push Fluids",
  "Restrict Fluids",
  "Nectar Thick",
  "Honey Thick",
  "Pudding Thick",
  "Straw",
  "Glass with Lid",
];
const MOOD_PROGRAM_OPTIONS = [
  "Crisis Plan",
  "Safety Plan",
  "ECS (Contracted/MEDICAID)",
  "SBS (Contracted/MEDICAID)",
  "Meaningful Day (MEDICAID)",
];
const MENTAL_HEALTH_CONT_OPTIONS = [
  "Hallucinations",
  "Delusions",
  "Self Harm",
  "Suicidal Ideation",
  "Attempts",
  "Yes",
  "No",
  "Hospitalized for SI?",
];
const SERVICE_EXTRA_ARRAY_KEYS = [
  "skinBodyAssessmentFlags",
  "medicationSupportFlags",
  "mobilityFlags",
  "toiletingFlags",
  "behaviorFlags",
  "moodFlags",
  "mentalHealthFlags",
  "transportationFlags",
  "evacuationFlags",
  "telephoneFlags",
  "skinPreferenceFlags",
  "communicationFlags",
  "hearingFlags",
  "visionFlags",
  "hygieneFlags",
  "mouthFlags",
  "medicalDeviceFlags",
  "transferFlags",
  "bathingFlags",
  "dressingFlags",
  "eatingFlags",
  "drinkingFlags",
];

const PRELIM_SERVICE_PLAN_HEADERS = [
  "SKIN AND BODY ASSESSMENT",
  "EVACUATION LEVEL",
  "TELEPHONE USE",
  "MEDICATION:",
  "LIKES/PREFERENCES/GOALS:",
  "DISLIKES/THINGS TO AVOID/TRIGGERS:",
  "SKIN",
  "COMMUNICATION",
  "HEARING",
  "VISION",
  "DEXTERITY",
  "MOBILITY/AMBULATION",
  "HYGIENE",
  "MOUTH",
  "TOILETING",
  "MEDICAL DEVICES",
  "TRANSFER/REPOSITION",
  "BATHING",
  "DRESSING",
  "EATING",
  "DRINKING",
  "ISSUES/BEHAVIORS",
  "MOOD",
  "MENTAL HEALTH CONT.",
  "OTHER CONCERNS:",
  "PCP/DR.:",
  "MHP:",
  "SOCIAL WORKER:",
  "TRANSPORTATION:",
];

function normalizeOcrText(raw = "") {
  return String(raw || "")
    .replace(/\r/g, "\n")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function extractSection(rawText, startMarkers = [], endMarkers = []) {
  const text = String(rawText || "");
  const upper = text.toUpperCase();
  let startIdx = -1;
  let pickedMarker = "";

  for (const marker of startMarkers) {
    const idx = upper.indexOf(String(marker || "").toUpperCase());
    if (idx >= 0 && (startIdx < 0 || idx < startIdx)) {
      startIdx = idx;
      pickedMarker = marker;
    }
  }
  if (startIdx < 0) return "";

  const contentStart = startIdx + String(pickedMarker || "").length;
  let endIdx = text.length;
  for (const marker of endMarkers) {
    const idx = upper.indexOf(String(marker || "").toUpperCase(), contentStart);
    if (idx >= 0 && idx < endIdx) {
      endIdx = idx;
    }
  }

  return text.slice(contentStart, endIdx).trim();
}

function parseAdmitDate(rawText = "") {
  const text = String(rawText || "");
  const datePatterns = [
    /ADMIT\s*DATE[^0-9A-Za-z]*([0-9]{1,2}[/-][0-9]{1,2}[/-][0-9]{2,4})/i,
    /ADMIT\s*DATE[^0-9A-Za-z]*([0-9]{4}[/-][0-9]{1,2}[/-][0-9]{1,2})/i,
  ];
  for (const pattern of datePatterns) {
    const match = text.match(pattern);
    if (match?.[1]) return match[1].trim();
  }
  return "";
}

function buildHeaderSectionSnapshots(rawText = "", headers = []) {
  const text = String(rawText || "");
  const upper = text.toUpperCase();
  const indexed = headers
    .map((header) => ({
      header,
      idx: upper.indexOf(String(header).toUpperCase()),
    }))
    .filter((item) => item.idx >= 0)
    .sort((a, b) => a.idx - b.idx);

  const snapshots = {};
  const ranges = [];
  indexed.forEach((item, i) => {
    const start = item.idx;
    const end = i + 1 < indexed.length ? indexed[i + 1].idx : text.length;
    const block = text.slice(start, end).trim();
    snapshots[item.header] = block;
    ranges.push([start, end]);
  });
  return { snapshots, ranges };
}

function buildUnmappedFromRanges(rawText = "", ranges = []) {
  const text = String(rawText || "");
  if (!text.trim() || !Array.isArray(ranges) || !ranges.length) return text.trim();
  const sorted = ranges
    .filter((r) => Array.isArray(r) && Number.isFinite(r[0]) && Number.isFinite(r[1]))
    .sort((a, b) => a[0] - b[0]);
  let cursor = 0;
  let out = "";
  sorted.forEach(([start, end]) => {
    if (start > cursor) out += `${text.slice(cursor, start)}\n`;
    cursor = Math.max(cursor, end);
  });
  if (cursor < text.length) out += text.slice(cursor);
  return normalizeOcrText(out);
}

function parseServicePlanOcr(rawText = "") {
  const clean = normalizeOcrText(rawText);
  const stopAll = PRELIM_SERVICE_PLAN_HEADERS;
  const { snapshots: sectionSnapshots, ranges: sectionRanges } = buildHeaderSectionSnapshots(
    clean,
    PRELIM_SERVICE_PLAN_HEADERS
  );
  const diagnosis = extractSection(
    clean,
    ["DIAGNOSIS/MEDICAL HISTORY", "DIAGNOSIS / MEDICAL HISTORY"],
    ["NOTES:", "SKIN AND BODY ASSESSMENT", ...stopAll]
  );
  const notes = extractSection(
    clean,
    ["NOTES:"],
    ["SKIN AND BODY ASSESSMENT", ...stopAll]
  );
  const allergies = extractSection(
    clean,
    ["ALLERGIES"],
    ["DIAGNOSIS/MEDICAL HISTORY", "DIAGNOSIS / MEDICAL HISTORY", "NOTES:", "SKIN AND BODY ASSESSMENT", ...stopAll]
  );
  const refusal = extractSection(
    clean,
    ["If the resident refuses care or services"],
    ["LIKES/PREFERENCES/GOALS:", ...stopAll]
  );

  return {
    admitDate: parseAdmitDate(clean),
    diagnosisMedicalHistory: diagnosis,
    notes: notes || allergies,
    allergiesOcr: allergies,
    evacuationLevel: extractSection(clean, ["EVACUATION LEVEL"], ["TELEPHONE USE", ...stopAll]),
    telephoneUseLevel: extractSection(clean, ["TELEPHONE USE"], ["MEDICATION:", ...stopAll]),
    medicationSupportLevel: extractSection(clean, ["MEDICATION:"], ["LIKES/PREFERENCES/GOALS:", ...stopAll]),
    likesPreferencesGoals: extractSection(clean, ["LIKES/PREFERENCES/GOALS:"], ["DISLIKES/THINGS TO AVOID/TRIGGERS:", ...stopAll]),
    dislikesTriggers: extractSection(clean, ["DISLIKES/THINGS TO AVOID/TRIGGERS:"], ["WAC 388-76-10340", "SKIN", ...stopAll]),
    skinBodyAssessment: extractSection(clean, ["SKIN AND BODY ASSESSMENT"], ["EVACUATION LEVEL", ...stopAll]),
    skinPreferences: extractSection(clean, ["SKIN"], ["COMMUNICATION", ...stopAll]),
    communication: extractSection(clean, ["COMMUNICATION"], ["HEARING", ...stopAll]),
    hearing: extractSection(clean, ["HEARING"], ["VISION", ...stopAll]),
    vision: extractSection(clean, ["VISION"], ["DEXTERITY", ...stopAll]),
    dexterityContracture: extractSection(clean, ["DEXTERITY"], ["MOBILITY/AMBULATION", ...stopAll]),
    mobilityAmbulation: extractSection(clean, ["MOBILITY/AMBULATION"], ["HYGIENE", ...stopAll]),
    hygiene: extractSection(clean, ["HYGIENE"], ["MOUTH", ...stopAll]),
    mouth: extractSection(clean, ["MOUTH"], ["TOILETING", ...stopAll]),
    toileting: extractSection(clean, ["TOILETING"], ["MEDICAL DEVICES", ...stopAll]),
    medicalDevices: extractSection(clean, ["MEDICAL DEVICES"], ["TRANSFER/REPOSITION", ...stopAll]),
    transferReposition: extractSection(clean, ["TRANSFER/REPOSITION"], ["BATHING", ...stopAll]),
    bathing: extractSection(clean, ["BATHING"], ["DRESSING", ...stopAll]),
    dressing: extractSection(clean, ["DRESSING"], ["EATING", ...stopAll]),
    eatingDiet: extractSection(clean, ["EATING"], ["DRINKING", ...stopAll]),
    drinking: extractSection(clean, ["DRINKING"], ["ISSUES/BEHAVIORS", ...stopAll]),
    issuesBehaviors: extractSection(clean, ["ISSUES/BEHAVIORS"], ["MOOD", ...stopAll]),
    mood: extractSection(clean, ["MOOD"], ["MENTAL HEALTH CONT.", ...stopAll]),
    mentalHealth: extractSection(clean, ["MENTAL HEALTH CONT."], ["OTHER CONCERNS:", ...stopAll]),
    otherConcerns: extractSection(clean, ["OTHER CONCERNS:"], ["PCP/DR.:", ...stopAll]),
    pcpDoctor: extractSection(clean, ["PCP/DR.:"], ["MHP:", ...stopAll]),
    mentalHealthProvider: extractSection(clean, ["MHP:"], ["SOCIAL WORKER:", ...stopAll]),
    socialWorker: extractSection(clean, ["SOCIAL WORKER:"], ["TRANSPORTATION:", ...stopAll]),
    transportation: extractSection(clean, ["TRANSPORTATION:"], ["LEGEND:", ...stopAll]),
    refusalSafetyPlan: refusal,
    resourcesContacts: extractSection(clean, ["RESOURCES:"], ["PCP/DR.:", ...stopAll]),
    transportationDetails: extractSection(clean, ["TRANSPORTATION:"], ["LEGEND:", ...stopAll]),
    wacReference: extractSection(clean, ["WAC 388-76-10340"], ["SKIN", ...stopAll]),
    legend: extractSection(clean, ["LEGEND:"], []),
    sectionSnapshots,
    unmappedText: buildUnmappedFromRanges(clean, sectionRanges),
    ocrRawText: clean.slice(0, 50000),
    ocrImportedAt: new Date().toISOString(),
  };
}

async function extractPdfText(file) {
  const buffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;
  let mergedText = "";

  for (let i = 1; i <= pdf.numPages; i += 1) {
    const page = await pdf.getPage(i);
    const textContent = await page.getTextContent();
    const pageText = textContent.items.map((item) => item.str || "").join(" ");
    if (pageText.trim().length > 30) {
      mergedText += `${pageText}\n`;
      continue;
    }
    const viewport = page.getViewport({ scale: 2 });
    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) continue;
    await page.render({ canvasContext: ctx, viewport }).promise;
    const {
      data: { text },
    } = await Tesseract.recognize(canvas, "eng");
    mergedText += `${text}\n`;
  }
  return mergedText.trim();
}

async function extractServicePlanFileText(file) {
  if (!file) return "";
  if (file.type === "application/pdf") {
    return extractPdfText(file);
  }
  const {
    data: { text },
  } = await Tesseract.recognize(file, "eng");
  return String(text || "");
}

const getDefaultServiceExtras = () => ({
  admitDate: "",
  diagnosisMedicalHistory: "",
  notes: "",
  evacuationLevel: "",
  evacuationFlags: [],
  evacuationInstructions: "",
  telephoneUseLevel: "",
  telephoneFlags: [],
  phoneSupportNotes: "",
  ownPhone: "",
  phoneNumber: "",
  medicationSupportLevel: "",
  medLockBoxLocation: "",
  usesPsychotropic: "",
  bslMonitoringFrequency: "",
  diabetesType: "",
  likesPreferencesGoals: "",
  dislikesTriggers: "",
  otherConcerns: "",
  pcpDoctor: "",
  mentalHealthProvider: "",
  socialWorker: "",
  transportation: "",
  skinBodyAssessmentFlags: [],
  skinPreferenceFlags: [],
  communicationFlags: [],
  hearingFlags: [],
  visionFlags: [],
  hygieneFlags: [],
  mouthFlags: [],
  medicationSupportFlags: [],
  mobilityFlags: [],
  toiletingFlags: [],
  behaviorFlags: [],
  moodFlags: [],
  mentalHealthFlags: [],
  transportationFlags: [],
  medicalDeviceFlags: [],
  transferFlags: [],
  bathingFlags: [],
  dressingFlags: [],
  eatingFlags: [],
  drinkingFlags: [],
  skinBodyAssessment: "",
  skinPreferences: "",
  communication: "",
  hearing: "",
  vision: "",
  dexterityContracture: "",
  mobilityAmbulation: "",
  hygiene: "",
  mouth: "",
  toileting: "",
  medicalDevices: "",
  transferReposition: "",
  bathing: "",
  dressing: "",
  eatingDiet: "",
  eatingOtherDiet: "",
  specialUtensilsEquipment: "",
  foodPreferences: "",
  drinkingPreferencesNotes: "",
  drinking: "",
  issuesBehaviors: "",
  behaviorOther: "",
  mood: "",
  moodOther1: "",
  moodOther2: "",
  moodOther3: "",
  mentalHealth: "",
  mentalHealthAttempts: "",
  mentalHealthHospitalizedDates: "",
  suicidePlan: "",
  refusalSafetyPlan: "",
  resourcesContacts: "",
  transportationDetails: "",
  restrictFluidsDetails: "",
  tubeFeedingFrequency: "",
  wacReference: "",
  legend: "",
  sectionSnapshots: {},
  unmappedText: "",
  ocrRawText: "",
  ocrImportedAt: "",
  allergiesOcr: "",
});

const parseServiceExtrasFromCarePlan = (carePlan) => {
  const defaults = getDefaultServiceExtras();
  const raw = carePlan?.preliminaryServicePlan;
  if (!raw) return defaults;
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    const normalized = {
      ...defaults,
      ...(parsed || {}),
    };
    SERVICE_EXTRA_ARRAY_KEYS.forEach((key) => {
      normalized[key] = Array.isArray(parsed?.[key]) ? parsed[key] : [];
    });
    return normalized;
  } catch (_error) {
    // Backward compatibility: some residents may have preliminaryServicePlan stored as
    // raw text instead of JSON. Parse it like OCR so fields are still populated.
    if (typeof raw === "string" && raw.trim()) {
      const parsedFromText = parseServicePlanOcr(raw);
      return {
        ...defaults,
        ...parsedFromText,
        sectionSnapshots:
          parsedFromText?.sectionSnapshots &&
          typeof parsedFromText.sectionSnapshots === "object"
            ? parsedFromText.sectionSnapshots
            : {},
        ocrRawText: parsedFromText?.ocrRawText || raw,
      };
    }
    return defaults;
  }
};

/* ================= SHARED SECTION ================= */

const Section = ({ title, action, children, sx, className }) => (
  <Paper elevation={1} className={className} sx={{ p: 2, mb: 3, ...(sx || {}) }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
      <Typography fontWeight={600}>
        {title}
      </Typography>
      {action || null}
    </Stack>
    <Divider sx={{ mb: 2 }} />
    {children}
  </Paper>
);

const ChecklistField = ({
  title,
  options = [],
  value = [],
  onChange,
  disabled = false,
  columns = 1,
}) => {
  const xs = columns === 3 ? 4 : columns === 2 ? 6 : 12;
  const safeValue = Array.isArray(value) ? value : [];
  const toggleOption = (option, checked) => {
    if (!onChange) return;
    if (checked) {
      onChange([...safeValue, option]);
      return;
    }
    onChange(safeValue.filter((item) => item !== option));
  };
  return (
    <Paper variant="outlined" sx={{ p: 1.25, height: "100%" }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
        {title}
      </Typography>
      <FormGroup>
        <Grid container>
          {options.map((option) => (
            <Grid
              item
              xs={12}
              md={xs}
              key={`${title}-${option}`}
              sx={{
                borderBottom: "1px solid",
                borderColor: "divider",
                py: 0.25,
              }}
            >
              <FormControlLabel
                sx={{
                  alignItems: "center",
                  m: 0,
                  mr: 0.5,
                  minHeight: 26,
                  "& .MuiFormControlLabel-label": {
                    fontSize: "0.84rem",
                    lineHeight: 1.3,
                    whiteSpace: "normal",
                    overflowWrap: "anywhere",
                    marginTop: 0,
                    fontWeight: 500,
                    pr: 0.5,
                  },
                  "& .MuiCheckbox-root": {
                    p: "2px 6px 2px 0",
                    alignSelf: "center",
                  },
                }}
                control={
                  <Checkbox
                    size="small"
                    checked={safeValue.includes(option)}
                    disabled={disabled}
                    onChange={(e) => toggleOption(option, e.target.checked)}
                  />
                }
                label={option}
              />
            </Grid>
          ))}
        </Grid>
      </FormGroup>
    </Paper>
  );
};

/* ================= COMPONENT ================= */

export default function CarePlanTab({ resident, onCarePlanSaved }) {
  /* ---------- REFS ---------- */
  const printRef = useRef();
  /* ---------- STATE ---------- */
  const [editingBase, setEditingBase] = useState(false);
  const [dirtyBase, setDirtyBase] = useState(false);
  const [lockNotice, setLockNotice] = useState({
    open: false,
    message: "",
    nonce: 0,
  });

  const [basePlan, setBasePlan] = useState({
    primaryDiagnosis: "",
    secondaryDiagnoses: [],
    allergies: [],
    specialDiet: "",
    riskLevel: "",
    riskFactors: [],
    mitigationStrategy: "",
    mobilityStatus: "",
    cognitiveStatus: "",
    codeStatus: "",
  });
  const [savedBasePlan, setSavedBasePlan] = useState({
    primaryDiagnosis: "",
    secondaryDiagnoses: [],
    allergies: [],
    specialDiet: "",
    riskLevel: "",
    riskFactors: [],
    mitigationStrategy: "",
    mobilityStatus: "",
    cognitiveStatus: "",
    codeStatus: "",
  });

  const [secondaryDiagnosesText, setSecondaryDiagnosesText] = useState("");
  const [allergiesText, setAllergiesText] = useState("");
  const [serviceExtras, setServiceExtras] = useState(getDefaultServiceExtras());
  const [savedServiceExtras, setSavedServiceExtras] = useState(getDefaultServiceExtras());
  const [ocrFile, setOcrFile] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrStatus, setOcrStatus] = useState("");
  const showLockNotice = (message) => {
    setLockNotice({
      open: true,
      message:
        message || "This section is read-only. Click Edit Base Plan to make changes.",
      nonce: Date.now(),
    });
  };
  const lockedFieldProps = (editable, message) =>
    editable
      ? {}
      : {
        InputProps: {
          readOnly: true,
          inputProps: { tabIndex: -1 },
        },
        onMouseDown: (e) => {
          e.preventDefault();
          showLockNotice(message);
        },
        onFocus: (e) => {
          e.target.blur();
        },
        onKeyDown: (e) => {
          const allowed = [
            "Tab",
            "Shift",
            "ArrowLeft",
            "ArrowRight",
            "ArrowUp",
            "ArrowDown",
          ];
          if (allowed.includes(e.key)) return;
          e.preventDefault();
          showLockNotice(message);
        },
        sx: { "& .MuiInputBase-input": { cursor: "not-allowed" } },
      };
  /* ---------- LOAD MOCK DATA ---------- */
  useEffect(() => {
    if (!resident) return;

    const nextBasePlan = {
      primaryDiagnosis: resident.carePlan?.primaryDiagnosis || "",
      secondaryDiagnoses: resident.carePlan?.secondaryDiagnoses || [],
      allergies: resident.carePlan?.allergies || [],
      specialDiet: resident.carePlan?.specialDiet || "",
      riskLevel: resident.carePlan?.riskLevel || "",
      riskFactors: resident.carePlan?.riskFactors || [],
      mitigationStrategy: resident.carePlan?.mitigationStrategy || "",
      mobilityStatus: resident.carePlan?.mobilityStatus || "",
      cognitiveStatus: resident.carePlan?.cognitiveStatus || "",
      codeStatus: resident.carePlan?.codeStatus || "",
    };
    setBasePlan(nextBasePlan);
    setSavedBasePlan(nextBasePlan);
    setSecondaryDiagnosesText(
      (resident.carePlan?.secondaryDiagnoses || []).join(", ")
    );
    setAllergiesText((resident.carePlan?.allergies || []).join(", "));

    const parsedExtras = parseServiceExtrasFromCarePlan(resident?.carePlan);
    setServiceExtras(parsedExtras);
    setSavedServiceExtras(parsedExtras);
  }, [resident]);
  const [saveCarePlan] = useMutation(SAVE_CARE_PLAN);
  /* ---------- PRINT ---------- */
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Preliminary_Care_Plan_${resident?.lastName || ""}`,
  });
  if (!resident) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">Loading care planÃ¢â‚¬Â¦</Typography>
      </Box>
    );
  }
  const residentDisplayName =
    [resident?.firstName, resident?.middleName, resident?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim() || `Resident #${resident?.id || ""}`;
  const printedAt = new Date().toLocaleString();
  const handleServiceExtraChange = (key, value) => {
    setServiceExtras((prev) => ({ ...prev, [key]: value }));
    setDirtyBase(true);
  };
  const withServicePlanLock = (children) => (
    <Box
      onMouseDown={(e) => {
        if (editingBase) return;
        e.preventDefault();
        showLockNotice("Click Edit Base Plan to update Preliminary Service Plan fields.");
      }}
    >
      {children}
    </Box>
  );
  const renderChecklist = (title, options, key, columns = 2) =>
    withServicePlanLock(
      <ChecklistField
        title={title}
        options={options}
        value={serviceExtras[key] || []}
        disabled={!editingBase}
        columns={columns}
        onChange={(next) => handleServiceExtraChange(key, next)}
      />
    );
  const handleImportServicePlanOcr = async () => {
    if (!ocrFile) {
      setOcrStatus("Select a file first.");
      return;
    }
    setOcrLoading(true);
    setOcrStatus("Running OCR and mapping fields...");
    try {
      const rawText = await extractServicePlanFileText(ocrFile);
      const parsed = parseServicePlanOcr(rawText);
      setServiceExtras((prev) => ({
        ...prev,
        ...parsed,
      }));
      if (!editingBase) {
        setEditingBase(true);
      }
      if (!allergiesText?.trim() && parsed?.allergiesOcr) {
        setAllergiesText(parsed.allergiesOcr);
      }
      if (!secondaryDiagnosesText?.trim() && parsed?.diagnosisMedicalHistory) {
        setSecondaryDiagnosesText(parsed.diagnosisMedicalHistory);
      }
      setDirtyBase(true);
      setOcrStatus("OCR import complete. Review fields before saving.");
    } catch (err) {
      console.error("Service plan OCR import failed:", err);
      setOcrStatus("OCR failed. Try a clearer image/PDF.");
    } finally {
      setOcrLoading(false);
    }
  };
  const resetServiceExtras = () => {
    setServiceExtras(savedServiceExtras || getDefaultServiceExtras());
  };
  /* ================= RENDER ================= */

  return (
    <Box sx={{ p: 3 }}>
      {/* ================= HEADER (NO PRINT) ================= */}
      <Box className="no-print" sx={{ "@media print": { display: "none !important" } }}>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Preliminary Service Plan
          </Typography>

          <Stack direction="row" spacing={1}>
            <Button
              startIcon={<PrintIcon />}
              variant="outlined"
              onClick={handlePrint}
            >
              Print
            </Button>

            {!editingBase && (
              <Button
                startIcon={<EditIcon />}
                variant="contained"
                onClick={() => setEditingBase(true)}
              >
                Edit Base Plan
              </Button>
            )}

            {editingBase && (
              <>
                <Button
                  startIcon={<SaveIcon />}
                  variant="contained"
                  disabled={!dirtyBase}
                  onClick={async () => {
                    if (!resident?.id) return;
                    try {
                      const normalizedSecondaryDiagnoses =
                        parseCommaSeparatedList(secondaryDiagnosesText);
                      const normalizedAllergies =
                        parseCommaSeparatedList(allergiesText);
                      await saveCarePlan({
                        variables: {
                          data: {
                            residentId: resident.id,
                            primaryDiagnosis: basePlan.primaryDiagnosis || null,
                            secondaryDiagnoses: normalizedSecondaryDiagnoses,
                            allergies: normalizedAllergies,
                            specialDiet: basePlan.specialDiet || null,
                            riskLevel: basePlan.riskLevel || null,
                            riskFactors: basePlan.riskFactors || [],
                            mitigationStrategy: basePlan.mitigationStrategy || null,
                            mobilityStatus: basePlan.mobilityStatus || null,
                            cognitiveStatus: basePlan.cognitiveStatus || null,
                            codeStatus: basePlan.codeStatus || null,
                            preliminaryServicePlan: JSON.stringify(serviceExtras || {}),
                          },
                        },
                      });
                      const nextBasePlan = {
                        ...basePlan,
                        secondaryDiagnoses: normalizedSecondaryDiagnoses,
                        allergies: normalizedAllergies,
                      };
                      setBasePlan(nextBasePlan);
                      setSavedBasePlan(nextBasePlan);
                      setSecondaryDiagnosesText(
                        normalizedSecondaryDiagnoses.join(", ")
                      );
                      setAllergiesText(normalizedAllergies.join(", "));
                      if (typeof onCarePlanSaved === "function") {
                        await onCarePlanSaved();
                      }
                      setSavedServiceExtras({
                        ...(serviceExtras || getDefaultServiceExtras()),
                      });
                      setEditingBase(false);
                      setDirtyBase(false);
                    } catch (err) {
                      console.error("Failed to save care plan:", err);
                      alert("Failed to save care plan");
                    }
                  }}
                >
                  Save
                </Button>
                <Button
                  startIcon={<CloseIcon />}
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    setEditingBase(false);
                    setBasePlan(savedBasePlan);
                    setSecondaryDiagnosesText(
                      (savedBasePlan.secondaryDiagnoses || []).join(", ")
                    );
                    setAllergiesText((savedBasePlan.allergies || []).join(", "));
                    resetServiceExtras();
                    setDirtyBase(false);
                  }}
                >
                  Cancel
                </Button>
              </>
            )}
          </Stack>
        </Stack>
      </Box>

      <Box
        className="no-print"
        sx={{
          mb: 2,
          "@media print": { display: "none !important" },
        }}
      >
        <Section title="Preliminary Service Plan Intake (Upload Document)">
          <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ xs: "stretch", md: "center" }}>
            <Button variant="outlined" component="label" disabled={ocrLoading}>
              {ocrFile ? `Selected: ${ocrFile.name}` : "Choose Preliminary Service Plan File"}
              <input
                type="file"
                hidden
                accept=".pdf,image/*"
                onChange={(e) => {
                  const nextFile = e.target.files?.[0] || null;
                  setOcrFile(nextFile);
                  setOcrStatus(nextFile ? "File selected. Click Load Preliminary Service Plan (OCR)." : "");
                }}
              />
            </Button>
            <Button
              variant="contained"
              onClick={handleImportServicePlanOcr}
              disabled={ocrLoading || !ocrFile}
            >
              {ocrLoading ? "Loading..." : "Load Preliminary Service Plan (OCR)"}
            </Button>
            {!!ocrStatus && (
              <Typography variant="body2" color="text.secondary">
                {ocrStatus}
              </Typography>
            )}
          </Stack>
        </Section>
      </Box>

      {/* ================= PRINTABLE CONTENT ================= */}
      <Box ref={printRef}>
        <style>
          {`
            @media print {
              @page { size: landscape; margin: 10mm; }
              .psp-page {
                page-break-after: always;
                break-after: page;
                min-height: 180mm;
              }
              .psp-page:last-child {
                page-break-after: auto;
                break-after: auto;
              }
            }
          `}
        </style>
        <Box
          className="print-fixed-header"
          sx={{
            display: "none",
            "@media print": {
              display: "block",
            },
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Form: Preliminary Service Plan | Resident: {residentDisplayName}
            </Typography>
            <Typography variant="caption">Printed: {printedAt}</Typography>
          </Stack>
        </Box>
        <Box className="print-content-offset">
                <Section
          title="Page 1 of 3 - AFH Preliminary Service Plan"
          className="psp-page"
          sx={{ "@media print": { mb: 0 } }}
        >
          <Grid container rowSpacing={2} columnSpacing={{ xs: 1, md: 2.5 }} sx={{ "& > .MuiGrid-item": { minWidth: 0 } }}>
            <Grid item xs={12} md={4}>
              <TextField label="Resident Name" fullWidth size="small" value={residentDisplayName} InputProps={{ readOnly: true }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField label="Admit Date" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={serviceExtras.admitDate || ""} onChange={(e) => handleServiceExtraChange("admitDate", e.target.value)} {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField label="Allergies" fullWidth size="small" value={allergiesText} onChange={(e) => { setAllergiesText(e.target.value); setDirtyBase(true); }} {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Diagnosis / Medical History" fullWidth multiline minRows={4} size="small" value={serviceExtras.diagnosisMedicalHistory || ""} onChange={(e) => handleServiceExtraChange("diagnosisMedicalHistory", e.target.value)} {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Notes" fullWidth multiline minRows={4} size="small" value={serviceExtras.notes || ""} onChange={(e) => handleServiceExtraChange("notes", e.target.value)} {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")} />
            </Grid>
            <Grid item xs={12} md={6}>{renderChecklist("Skin and Body Assessment", SKIN_BODY_ASSESSMENT_OPTIONS, "skinBodyAssessmentFlags")}</Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Skin and Body Notes" fullWidth multiline minRows={5} size="small" value={serviceExtras.skinBodyAssessment || ""} onChange={(e) => handleServiceExtraChange("skinBodyAssessment", e.target.value)} {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")} />
            </Grid>
            <Grid item xs={12} md={4}>
              {renderChecklist("Evacuation Level", EVACUATION_FLAG_OPTIONS, "evacuationFlags")}
              <TextField label="Special Instructions" fullWidth size="small" sx={{ mt: 1 }} value={serviceExtras.evacuationInstructions || ""} onChange={(e) => handleServiceExtraChange("evacuationInstructions", e.target.value)} {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")} />
            </Grid>
            <Grid item xs={12} md={4}>
              {renderChecklist("Medication", MEDICATION_SUPPORT_OPTIONS, "medicationSupportFlags")}
              <TextField label="Name/# of RND" fullWidth size="small" sx={{ mt: 1 }} value={serviceExtras.medicationSupportLevel || ""} onChange={(e) => handleServiceExtraChange("medicationSupportLevel", e.target.value)} {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")} />
              <TextField label="Lock Box in Room" fullWidth size="small" sx={{ mt: 1 }} value={serviceExtras.medLockBoxLocation || ""} onChange={(e) => handleServiceExtraChange("medLockBoxLocation", e.target.value)} {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField label="Likes / Preferences / Goals" fullWidth multiline minRows={5} size="small" value={serviceExtras.likesPreferencesGoals || ""} onChange={(e) => handleServiceExtraChange("likesPreferencesGoals", e.target.value)} {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")} />
              <TextField label="Dislikes / Things to Avoid / Triggers" fullWidth multiline minRows={5} size="small" sx={{ mt: 1 }} value={serviceExtras.dislikesTriggers || ""} onChange={(e) => handleServiceExtraChange("dislikesTriggers", e.target.value)} {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")} />
            </Grid>
            <Grid item xs={12} md={4}>
              {renderChecklist("Telephone Use", TELEPHONE_FLAG_OPTIONS, "telephoneFlags")}
              <TextField label="Phone Number" fullWidth size="small" sx={{ mt: 1 }} value={serviceExtras.phoneNumber || ""} onChange={(e) => handleServiceExtraChange("phoneNumber", e.target.value)} {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")} />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField label="Refusal/Safety Statement" fullWidth multiline minRows={6} size="small" value={serviceExtras.refusalSafetyPlan || ""} onChange={(e) => handleServiceExtraChange("refusalSafetyPlan", e.target.value)} {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")} />
            </Grid>
          </Grid>
          <Typography variant="caption" sx={{ display: "block", mt: 1, fontWeight: 600 }}>
            WAC 388-76-10340: Preliminary service plan must include assessed needs, refusal/safety approach,
            resident goals/preferences, and how needs will be met.
          </Typography>
          <Typography variant="caption" sx={{ display: "block", mt: 2, fontWeight: 600 }}>Page 1 of 3</Typography>
        </Section>

        <Section
          title="Page 2 of 3 - Functional Status"
          className="psp-page"
          sx={{ "@media print": { mb: 0 } }}
        >
          <Grid container rowSpacing={2} columnSpacing={{ xs: 1, md: 2.5 }} sx={{ "& > .MuiGrid-item": { minWidth: 0 } }}>
            <Grid item xs={12} md={3}>{renderChecklist("Skin", SKIN_PREFERENCE_OPTIONS, "skinPreferenceFlags")}<TextField label="Preference/Frequency" fullWidth size="small" sx={{ mt: 1 }} value={serviceExtras.skinPreferences || ""} onChange={(e) => handleServiceExtraChange("skinPreferences", e.target.value)} {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")} /></Grid>
            <Grid item xs={12} md={3}>{renderChecklist("Communication", COMMUNICATION_FLAG_OPTIONS, "communicationFlags")}<TextField label="Notes" fullWidth multiline minRows={3} size="small" sx={{ mt: 1 }} value={serviceExtras.communication || ""} onChange={(e) => handleServiceExtraChange("communication", e.target.value)} {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")} /></Grid>
            <Grid item xs={12} md={3}>{renderChecklist("Hearing", HEARING_FLAG_OPTIONS, "hearingFlags")}<TextField label="Notes" fullWidth multiline minRows={3} size="small" sx={{ mt: 1 }} value={serviceExtras.hearing || ""} onChange={(e) => handleServiceExtraChange("hearing", e.target.value)} {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")} /></Grid>
            <Grid item xs={12} md={3}>{renderChecklist("Vision", VISION_FLAG_OPTIONS, "visionFlags")}<TextField label="Dexterity / Contracture" fullWidth multiline minRows={3} size="small" sx={{ mt: 1 }} value={serviceExtras.dexterityContracture || ""} onChange={(e) => handleServiceExtraChange("dexterityContracture", e.target.value)} {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")} /></Grid>
            <Grid item xs={12} md={3}>{renderChecklist("Mobility / Ambulation", MOBILITY_FLAG_OPTIONS, "mobilityFlags")}<TextField label="Notes" fullWidth multiline minRows={3} size="small" sx={{ mt: 1 }} value={serviceExtras.mobilityAmbulation || ""} onChange={(e) => handleServiceExtraChange("mobilityAmbulation", e.target.value)} {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")} /></Grid>
            <Grid item xs={12} md={3}>{renderChecklist("Hygiene", HYGIENE_FLAG_OPTIONS, "hygieneFlags")}<TextField label="Preference/Frequency" fullWidth size="small" sx={{ mt: 1 }} value={serviceExtras.hygiene || ""} onChange={(e) => handleServiceExtraChange("hygiene", e.target.value)} {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")} /></Grid>
            <Grid item xs={12} md={3}>{renderChecklist("Mouth", MOUTH_FLAG_OPTIONS, "mouthFlags")}<TextField label="Preference/Frequency" fullWidth size="small" sx={{ mt: 1 }} value={serviceExtras.mouth || ""} onChange={(e) => handleServiceExtraChange("mouth", e.target.value)} {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")} /></Grid>
            <Grid item xs={12} md={3}>{renderChecklist("Toileting", TOILETING_FLAG_OPTIONS, "toiletingFlags")}<TextField label="Frequency" fullWidth size="small" sx={{ mt: 1 }} value={serviceExtras.toileting || ""} onChange={(e) => handleServiceExtraChange("toileting", e.target.value)} {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")} /></Grid>
            <Grid item xs={12} md={3}>{renderChecklist("Medical Devices", MEDICAL_DEVICE_FLAG_OPTIONS, "medicalDeviceFlags")}<TextField label="Notes" fullWidth size="small" sx={{ mt: 1 }} value={serviceExtras.medicalDevices || ""} onChange={(e) => handleServiceExtraChange("medicalDevices", e.target.value)} {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")} /></Grid>
            <Grid item xs={12} md={3}>{renderChecklist("Transfer/Reposition", TRANSFER_FLAG_OPTIONS, "transferFlags")}<TextField label="PRN/Every Hours" fullWidth size="small" sx={{ mt: 1 }} value={serviceExtras.transferReposition || ""} onChange={(e) => handleServiceExtraChange("transferReposition", e.target.value)} {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")} /></Grid>
            <Grid item xs={12} md={3}>{renderChecklist("Bathing", BATHING_FLAG_OPTIONS, "bathingFlags")}<TextField label="Preference/Frequency" fullWidth size="small" sx={{ mt: 1 }} value={serviceExtras.bathing || ""} onChange={(e) => handleServiceExtraChange("bathing", e.target.value)} {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")} /></Grid>
            <Grid item xs={12} md={3}>{renderChecklist("Dressing", DRESSING_FLAG_OPTIONS, "dressingFlags")}<TextField label="Preference/Frequency" fullWidth size="small" sx={{ mt: 1 }} value={serviceExtras.dressing || ""} onChange={(e) => handleServiceExtraChange("dressing", e.target.value)} {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")} /></Grid>
          </Grid>
          <Typography variant="caption" sx={{ display: "block", mt: 1, fontWeight: 600 }}>
            See WAC 388-76-10650 for medical devices.
          </Typography>
          <Typography variant="caption" sx={{ display: "block", mt: 2, fontWeight: 600 }}>Page 2 of 3</Typography>
        </Section>

        <Section
          title="Page 3 of 3 - Nutrition / Behavior / Mental Health"
          className="psp-page"
          sx={{ "@media print": { mb: 0 } }}
        >
                    <Grid container rowSpacing={2} columnSpacing={{ xs: 1, md: 2.5 }} sx={{ "& > .MuiGrid-item": { minWidth: 0 } }}>
            <Grid item xs={12} md={3}>
              {renderChecklist("Eating", EATING_FLAG_OPTIONS, "eatingFlags")}
              <TextField
                label="DIET - OTHER"
                fullWidth
                size="small"
                variant="outlined"
                sx={{ mt: 1 }}
                value={serviceExtras.eatingOtherDiet || ""}
                onChange={(e) => handleServiceExtraChange("eatingOtherDiet", e.target.value)}
                {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")}
              />
              <TextField
                label="Special Utensils/Adaptive Equipment"
                fullWidth
                size="small"
                variant="outlined"
                sx={{ mt: 1 }}
                value={serviceExtras.specialUtensilsEquipment || ""}
                onChange={(e) => handleServiceExtraChange("specialUtensilsEquipment", e.target.value)}
                {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")}
              />
              <TextField
                label="Food Preferences"
                fullWidth
                multiline
                minRows={2}
                size="small"
                variant="outlined"
                sx={{ mt: 1 }}
                value={serviceExtras.foodPreferences || ""}
                onChange={(e) => handleServiceExtraChange("foodPreferences", e.target.value)}
                {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")}
              />
              <TextField
                label="Drinking Preferences"
                fullWidth
                multiline
                minRows={2}
                size="small"
                variant="outlined"
                sx={{ mt: 1 }}
                value={serviceExtras.drinkingPreferencesNotes || ""}
                onChange={(e) => handleServiceExtraChange("drinkingPreferencesNotes", e.target.value)}
                {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")}
              />
              {renderChecklist("Mental Health Cont.", MENTAL_HEALTH_CONT_OPTIONS, "mentalHealthFlags")}
              <TextField
                label="Dates"
                fullWidth
                size="small"
                variant="outlined"
                sx={{ mt: 1 }}
                value={serviceExtras.mentalHealthHospitalizedDates || ""}
                onChange={(e) => handleServiceExtraChange("mentalHealthHospitalizedDates", e.target.value)}
                {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")}
              />
              <TextField
                label="Suicide Plan(?)"
                fullWidth
                multiline
                minRows={2}
                size="small"
                variant="outlined"
                sx={{ mt: 1 }}
                value={serviceExtras.suicidePlan || ""}
                onChange={(e) => handleServiceExtraChange("suicidePlan", e.target.value)}
                {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              {renderChecklist("Drinking", DRINKING_FLAG_OPTIONS, "drinkingFlags")}
              <TextField
                label="Restrict Fluids"
                fullWidth
                multiline
                minRows={2}
                size="small"
                variant="outlined"
                sx={{ mt: 1 }}
                value={serviceExtras.restrictFluidsDetails || ""}
                onChange={(e) => handleServiceExtraChange("restrictFluidsDetails", e.target.value)}
                {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")}
              />
              <Typography variant="caption" sx={{ display: "block", mt: 1, fontWeight: 600 }}>
                Ensure you have a Dr./therapist order for supplements, special diets,
                food/drink alterations, restrictions, modifications, and parameters.
              </Typography>
              <TextField
                label="Tube Feeding (Frequency)"
                fullWidth
                multiline
                minRows={2}
                size="small"
                variant="outlined"
                sx={{ mt: 1 }}
                value={serviceExtras.tubeFeedingFrequency || ""}
                onChange={(e) => handleServiceExtraChange("tubeFeedingFrequency", e.target.value)}
                {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")}
              />
              <TextField
                label="Other Concerns"
                fullWidth
                multiline
                minRows={3}
                size="small"
                variant="outlined"
                sx={{ mt: 1 }}
                value={serviceExtras.otherConcerns || ""}
                onChange={(e) => handleServiceExtraChange("otherConcerns", e.target.value)}
                {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")}
              />
              <Typography variant="caption" sx={{ display: "block", mt: 1, fontWeight: 700 }}>
                IF SOMEONE BECOMES A DANGER TO THEMSELF OR OTHERS CALL 911 AND REPORT A MENTAL HEALTH CRISIS.
              </Typography>
              <Typography variant="caption" sx={{ display: "block", mt: 0.5, fontWeight: 700 }}>
                CG IS TO FOLLOW THE ASSESSMENT, INDIVIDUAL CRISIS AND SAFETY PLANS.
              </Typography>
              <Typography variant="caption" sx={{ display: "block", mt: 0.5, fontWeight: 700 }}>
                REPORT CONCERNS IMMEDIATELY.
              </Typography>
              <Typography variant="caption" sx={{ display: "block", mt: 1, fontWeight: 700 }}>
                RESOURCES: NAMI 1-800-273-8255 | DSHS HOTLINE 1-800-562-6078
              </Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              {renderChecklist("Issues/Behaviors", BEHAVIOR_FLAG_OPTIONS, "behaviorFlags")}
              <TextField
                label="OTHER"
                fullWidth
                size="small"
                variant="outlined"
                sx={{ mt: 1 }}
                value={serviceExtras.behaviorOther || ""}
                onChange={(e) => handleServiceExtraChange("behaviorOther", e.target.value)}
                {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")}
              />
              <TextField
                label="PCP/DR."
                fullWidth
                multiline
                minRows={2}
                size="small"
                variant="outlined"
                sx={{ mt: 1 }}
                value={serviceExtras.pcpDoctor || ""}
                onChange={(e) => handleServiceExtraChange("pcpDoctor", e.target.value)}
                {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")}
              />
              <TextField
                label="MHP"
                fullWidth
                multiline
                minRows={2}
                size="small"
                variant="outlined"
                sx={{ mt: 1 }}
                value={serviceExtras.mentalHealthProvider || ""}
                onChange={(e) => handleServiceExtraChange("mentalHealthProvider", e.target.value)}
                {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")}
              />
              <TextField
                label="SOCIAL WORKER"
                fullWidth
                multiline
                minRows={2}
                size="small"
                variant="outlined"
                sx={{ mt: 1 }}
                value={serviceExtras.socialWorker || ""}
                onChange={(e) => handleServiceExtraChange("socialWorker", e.target.value)}
                {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")}
              />
              <TextField
                label="OTHER"
                fullWidth
                multiline
                minRows={5}
                size="small"
                variant="outlined"
                sx={{ mt: 1 }}
                value={serviceExtras.resourcesContacts || ""}
                onChange={(e) => handleServiceExtraChange("resourcesContacts", e.target.value)}
                {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              {renderChecklist("Mood", MOOD_FLAG_OPTIONS, "moodFlags")}
              <TextField label="OTHER" fullWidth size="small" variant="outlined" sx={{ mt: 1 }} value={serviceExtras.moodOther1 || ""} onChange={(e) => handleServiceExtraChange("moodOther1", e.target.value)} {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")} />
              <TextField label="OTHER" fullWidth size="small" variant="outlined" sx={{ mt: 1 }} value={serviceExtras.moodOther2 || ""} onChange={(e) => handleServiceExtraChange("moodOther2", e.target.value)} {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")} />
              <TextField label="OTHER" fullWidth size="small" variant="outlined" sx={{ mt: 1 }} value={serviceExtras.moodOther3 || ""} onChange={(e) => handleServiceExtraChange("moodOther3", e.target.value)} {...lockedFieldProps(editingBase, "Click Edit Base Plan to update form.")} />
              {renderChecklist("Programs/Plans", MOOD_PROGRAM_OPTIONS, "mentalHealthFlags")}
              <Typography variant="caption" sx={{ display: "block", mt: 1, fontWeight: 700 }}>
                CG TO INDICATE IN [ ]: EA = EASILY ALTERED, NEA = NOT EASILY ALTERED
              </Typography>
              {renderChecklist("Transportation", TRANSPORTATION_FLAG_OPTIONS, "transportationFlags")}
              <Typography variant="caption" sx={{ display: "block", mt: 1, fontWeight: 700 }}>
                LEGEND: RND = REGISTERED NURSE DELEGATOR | MRY = MEMORY | CG = CAREGIVER |
                PCP = PRIMARY CARE PHYSICIAN | DR. = DOCTOR | MHP = MENTAL HEALTH PROVIDER |
                CONT. = CONTINUE | SI = SUICIDAL IDEATION | L = LEFT | R = RIGHT
              </Typography>
            </Grid>
          </Grid>
          <Typography variant="caption" sx={{ display: "block", mt: 2, fontWeight: 600 }}>Page 3 of 3</Typography>
        </Section>

        </Box>
      </Box>
      <Snackbar
        key={lockNotice.nonce}
        open={lockNotice.open}
        autoHideDuration={3000}
        onClose={(_, reason) => {
          if (reason === "clickaway") return;
          setLockNotice((prev) => ({ ...prev, open: false }));
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="info"
          variant="filled"
          onClose={() => setLockNotice((prev) => ({ ...prev, open: false }))}
        >
          {lockNotice.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}


